import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  EditableToken,
  SectionContextProvider,
} from '@vibesites/runtime/react';

// ─── Bedroom pre-filter ─────────────────────────────────────────────────────
//
// The ApartmentSync (Repli360 / RRAC) widget is a third-party script with no
// supported API to pre-filter by bedroom count. The Hero's bedroom buttons
// deep-link here as `/floor-plans?bedroom=1` (or `?bedroom=studio`); we read that
// value and hide the floorplan cards the widget renders that don't match.
//
// This reads the widget's rendered markup, so it is best-effort and fails
// open: if no card is positively identified as the requested count, nothing
// is hidden (better to show everything than an empty widget).

const HIDE_ATTR = 'data-bedfilter-hidden';
const FLOORPLAN_ITEM_SELECTOR = '.floorPlanItem';

function targetBedsFromUrl(): number | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('bedroom');
  if (!raw) return null;
  const v = raw.trim().toLowerCase();
  if (v === 'studio' || v === 'std' || v === '0') return 0;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? null : n;
}

// Bedroom counts mentioned in a card's text. "Studio" → 0; "1 Bed" /
// "2 Bedrooms" / "1 BR" → the digit(s). A range like "1 - 2 Beds" yields both.
function detectBedCounts(text: string): number[] {
  const t = text.toLowerCase();
  const counts = new Set<number>();
  if (/\bstudio\b/.test(t)) counts.add(0);
  const re = /(\d+)\s*(?:bed|bedroom|bedrooms|bd|br)\b/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(t)) !== null) counts.add(parseInt(m[1], 10));
  return [...counts];
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ApartmentSync() {
  const blockSlot = useBlockSlot('apartmentsync.block', {
    defaultValue: 'apartment-sync',
  });

  const sectionRef = useRef<HTMLElement>(null);
  const [targetBeds] = useState<number | null>(() => targetBedsFromUrl());

  useEffect(() => {
    if (targetBeds === null) return;
    const root = sectionRef.current;
    if (!root) return;

    const apply = () => {
      const items = Array.from(
        root.querySelectorAll<HTMLElement>(FLOORPLAN_ITEM_SELECTOR),
      );
      if (items.length === 0) return;

      const decisions = items.map((el) => {
        const counts = detectBedCounts(el.textContent || '');
        return { el, counts, isMatch: counts.includes(targetBeds) };
      });

      // Fail open: if nothing positively matches the requested count, the
      // heuristic likely missed — reveal everything rather than hide all.
      const anyMatch = decisions.some((d) => d.isMatch);

      for (const { el, counts, isMatch } of decisions) {
        // Hide only cards we positively identified as a different count.
        const shouldHide = anyMatch && counts.length > 0 && !isMatch;
        if (shouldHide) {
          el.style.display = 'none';
          el.setAttribute(HIDE_ATTR, '1');
        } else if (el.hasAttribute(HIDE_ATTR)) {
          el.style.display = '';
          el.removeAttribute(HIDE_ATTR);
        }
      }
    };

    apply();
    // The widget renders asynchronously and re-renders when its own filters
    // change; re-apply on any DOM change inside our section. We observe only
    // childList/subtree (not attributes), so our own style writes don't loop.
    const observer = new MutationObserver(() => apply());
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [targetBeds]);

  return (
    <section ref={sectionRef} className="bg-white py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {targetBeds !== null && (
          <div className="mb-6 flex flex-wrap items-center justify-center gap-3 font-body text-sm text-[var(--vibe-color-text)]/70">
            <span>
              Showing{' '}
              {targetBeds === 0 ? 'studio' : `${targetBeds} bedroom`} floor
              plans
            </span>
            <a
              href="/floor-plans"
              className="underline underline-offset-4 hover:text-[var(--vibe-color-text)]"
            >
              Show all
            </a>
          </div>
        )}
        <EditableToken
          path="apartmentsync.block"
          type="block"
          label="Floor Plans Block"
          acceptedBlockCategories={['Integrations']}
          as="div"
        >
          {blockSlot ? (
            <SectionContextProvider sectionId={blockSlot.sectionId}>
              <blockSlot.BlockComponent sectionId={blockSlot.sectionId} />
            </SectionContextProvider>
          ) : (
            <div className="text-center font-body text-[var(--vibe-color-text)]/60">
              No floor plans block selected
            </div>
          )}
        </EditableToken>
      </div>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'ApartmentSync',
  displayName: 'Apartment Sync',
  tokens: {
    'apartmentsync.block': {
      label: 'Floor Plans Block',
      type: 'block',
      defaultValue: 'apartment-sync',
      acceptedBlockCategories: ['Integrations'],
    },
  },
};

(ApartmentSync as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ApartmentSync;
